import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { User } from "lucide-react";
import { MAIN_URL } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { ensurePersonPhotoDirectory, matchPersonPhoto } from "./personPhotoDirectory";

export function buildListProfileImageUrl(raw) {
  if (raw == null) return "";
  const t = String(raw).trim();
  if (
    !t ||
    t === "-" ||
    t.toLowerCase() === "null" ||
    t.toLowerCase() === "undefined"
  ) {
    return "";
  }
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  if (t.startsWith("/storage/app/public")) return `${MAIN_URL}${t}`;
  if (t.startsWith("/storage/")) return `${MAIN_URL}${t}`;
  if (t.startsWith("storage/")) return `${MAIN_URL}/${t}`;
  if (
    t.startsWith("employees/") ||
    t.startsWith("persons/") ||
    t.startsWith("interns/") ||
    t.startsWith("recruitment/")
  ) {
    return `${MAIN_URL}/storage/app/public/${t}`;
  }
  return `${MAIN_URL}/storage/app/public/employees/${t}`;
}

const PROFILE_IMAGE_KEYS = [
  "profile_image_url",
  "photo_url",
  "image_url",
  "avatar_url",
  "person.profile_image_url",
  "organization_person.person.profile_image_url",
  "employee.org_persons.person.profile_image_url",
  "intern.org_persons.person.profile_image_url",
  "employee.organization_person.person.profile_image_url",
  "intern.organization_person.person.profile_image_url",
  "org_persons.person.profile_image_url",
];

function findProfileImageDeep(value, depth = 0) {
  if (value == null || depth > 5) return "";
  if (typeof value === "string") return "";
  if (typeof value !== "object") return "";

  if (!Array.isArray(value)) {
    for (const key of Object.keys(value)) {
      if (
        /profile_image_url|photo_url|avatar_url|image_url/i.test(key) &&
        value[key]
      ) {
        const url = buildListProfileImageUrl(value[key]);
        if (url) return url;
      }
    }
    for (const nested of Object.values(value)) {
      if (nested && typeof nested === "object") {
        const found = findProfileImageDeep(nested, depth + 1);
        if (found) return found;
      }
    }
  }
  return "";
}

export function resolveRowProfileImageUrl(row, getNestedValue) {
  if (!row) return "";
  for (const key of PROFILE_IMAGE_KEYS) {
    const val = getNestedValue ? getNestedValue(row, key) : row[key];
    const url = buildListProfileImageUrl(val);
    if (url) return url;
  }
  return findProfileImageDeep(row);
}

const AVATAR_FALLBACK_BG = "linear-gradient(140deg, #3f4756, #6b7686)";
const AVATAR_FALLBACK_COLOR = "#ffffff";

/** Circular list avatar: profile photo when available, otherwise a people icon. */
export default function ListPersonAvatar({
  row,
  getNestedValue,
  imageUrl: imageUrlProp,
  size = 34,
  sx = {},
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [directoryUrl, setDirectoryUrl] = useState("");
  const orgId = useAuthStore((state) => state.userData?.organization?.organization_id);

  const imageUrl =
    imageUrlProp ||
    (row ? resolveRowProfileImageUrl(row, getNestedValue) : "");

  useEffect(() => {
    if (imageUrl || !row || !orgId) {
      setDirectoryUrl("");
      return undefined;
    }
    let active = true;
    ensurePersonPhotoDirectory(orgId).then((loaded) => {
      if (!active) return;
      const matched = matchPersonPhoto(loaded, row, getNestedValue);
      setDirectoryUrl(matched ? buildListProfileImageUrl(matched) : "");
    });
    return () => {
      active = false;
    };
  }, [imageUrl, orgId, row, getNestedValue]);

  const photoUrl = imageUrl || directoryUrl;

  const shellSx = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    borderRadius: "50%",
    flexShrink: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    background: AVATAR_FALLBACK_BG,
    color: AVATAR_FALLBACK_COLOR,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    ...sx,
  };

  if (photoUrl && !imageFailed) {
    return (
      <Box
        component="img"
        src={photoUrl}
        alt=""
        onError={() => setImageFailed(true)}
        sx={{
          ...shellSx,
          objectFit: "cover",
          background: AVATAR_FALLBACK_BG,
        }}
      />
    );
  }

  const iconSize = Math.max(14, Math.round(size * 0.48));
  return (
    <Box sx={shellSx} aria-hidden>
      <User size={iconSize} strokeWidth={2.25} />
    </Box>
  );
}
